// Owner Approval Queue — pending agent action approvals routed to the owner
// (or Director). The agent CANNOT see this widget (X-Actor-Type=agent → 403).
//
// Lane 2 gotcha: approving here does NOT immediately apply the gated action.
// The agent's NEXT API call against the engine consumes the approval. The
// success toast must say so to set operator expectations.

import { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  approveAgentApprovalRequest,
  denyAgentApprovalRequest,
  listAgentApprovalRequests,
} from '@/api/agentApprovalRequests';
import type {
  ApprovalListResponse,
  ApprovalRequestRead,
} from '@/api/agentBoardTypes';
import { EngineHttpError } from '@/lib/engineHttp';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { emitInspectTask } from '@/lib/agentBoardEvents';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

const SWR_KEY = 'agent-board:approvals:pending';
const REFRESH_MS = 15_000;

type RiskFilter = 'all' | 'low' | 'medium' | 'high';

const RISK_OPTIONS: { value: RiskFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const RISK_COLOR: Record<string, string> = {
  low: 'var(--sp-ok)',
  medium: 'var(--sp-warn)',
  high: 'var(--sp-error)',
};

function relativeTimeUntil(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return '';
  const abs = Math.abs(ms);
  const mins = Math.floor(abs / 60_000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  let core: string;
  if (days >= 1) core = `${days}d`;
  else if (hours >= 1) core = `${hours}h`;
  else core = `${mins}m`;
  return ms >= 0 ? `in ${core}` : `${core} ago`;
}

function fetcher(): Promise<ApprovalListResponse> {
  return listAgentApprovalRequests({ status: 'pending', limit: 50 });
}

interface ReasonModalState {
  request: ApprovalRequestRead;
  decision: 'approve' | 'deny';
}

function OwnerApprovalQueueWidget({ context: _context }: WidgetProps) {
  const { data, error, mutate, isLoading } = useSWR<ApprovalListResponse>(
    SWR_KEY,
    fetcher,
    { refreshInterval: REFRESH_MS, revalidateOnFocus: false },
  );

  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all');
  const [modal, setModal] = useState<ReasonModalState | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const items = useMemo(() => {
    const all = data?.items ?? [];
    if (riskFilter === 'all') return all;
    return all.filter((r) => (r.risk_level ?? '').toLowerCase() === riskFilter);
  }, [data?.items, riskFilter]);

  function openModal(request: ApprovalRequestRead, decision: 'approve' | 'deny') {
    setModal({ request, decision });
    setReason('');
    setActionError(null);
  }

  function closeModal() {
    setModal(null);
    setReason('');
    setSubmitting(false);
  }

  function flashToast(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 4000);
  }

  async function submitDecision() {
    if (!modal) return;
    const { request, decision } = modal;
    setSubmitting(true);
    setActionError(null);

    // Optimistic removal: drop the row from cache before the network resolves.
    const prev = data;
    const optimistic: ApprovalListResponse | undefined = prev
      ? {
          items: prev.items.filter((r) => r.id !== request.id),
          count: Math.max(0, prev.count - 1),
        }
      : prev;
    if (optimistic) {
      void mutate(optimistic, { revalidate: false });
    }

    try {
      if (decision === 'approve') {
        await approveAgentApprovalRequest(request.id, { reason: reason || undefined });
        flashToast(
          'Approval recorded. The agent consumes it on its next call.',
        );
      } else {
        await denyAgentApprovalRequest(request.id, { reason: reason || undefined });
        flashToast('Denied. The pending action will not be applied.');
      }
      void mutate();
      closeModal();
    } catch (err) {
      // Rollback on failure.
      if (prev) void mutate(prev, { revalidate: false });
      if (err instanceof EngineHttpError) {
        if (err.status === 409) {
          setActionError(
            'This request is no longer pending (already approved, denied, or expired).',
          );
        } else if (err.status === 403) {
          setActionError(
            'Forbidden — only the owning human or Director can resolve this request.',
          );
        } else {
          setActionError(`${err.status}: ${err.message}`);
        }
      } else {
        setActionError(err instanceof Error ? err.message : 'Unknown error');
      }
      setSubmitting(false);
    }
  }

  // ── Render branches ────────────────────────────────────────────

  const isForbidden = error instanceof EngineHttpError && error.status === 403;

  return (
    <div id="widget-owner-approval-queue" className="sp-widget">
      <div className="sp-widget-header">
        <span>Owner Approval Queue</span>
        <span style={{ fontSize: 11, fontWeight: 400, letterSpacing: 0, color: 'var(--sp-text-dim)' }}>
          {data ? `${items.length}/${data.count}` : ''}
        </span>
      </div>

      {/* Risk filter chips */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: '6px 12px',
          borderBottom: '1px solid var(--sp-border)',
        }}
      >
        {RISK_OPTIONS.map((opt) => {
          const active = riskFilter === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setRiskFilter(opt.value)}
              style={{
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 3,
                border: '1px solid var(--sp-border)',
                background: active ? 'var(--sp-accent)' : 'transparent',
                color: active ? 'var(--sp-bg, #000)' : 'var(--sp-text-muted)',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <div className="sp-widget-body" style={{ padding: 0 }}>
        {isForbidden ? (
          <div style={{ padding: 12, fontSize: 12 }}>
            <div style={{ color: 'var(--sp-error)' }}>
              403 — only the owning human or Director can view this queue.
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginTop: 4 }}>
              GET /api/v1/agent-approval-requests
            </div>
          </div>
        ) : error ? (
          <div style={{ padding: 12, fontSize: 12 }}>
            <div style={{ color: 'var(--sp-error)' }}>
              Failed to load: {error instanceof Error ? error.message : String(error)}
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginTop: 4 }}>
              GET /api/v1/agent-approval-requests
            </div>
          </div>
        ) : isLoading && !data ? (
          <div style={{ padding: 12, color: 'var(--sp-text-muted)', fontSize: 12 }}>
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 12, color: 'var(--sp-text-muted)', fontSize: 12 }}>
            No pending approvals.
          </div>
        ) : (
          <div>
            {items.map((req) => (
              <ApprovalRow key={req.id} request={req} onAct={openModal} />
            ))}
          </div>
        )}
      </div>

      {modal ? (
        <ReasonModal
          decision={modal.decision}
          request={modal.request}
          reason={reason}
          onReasonChange={setReason}
          onCancel={closeModal}
          onSubmit={submitDecision}
          submitting={submitting}
          error={actionError}
        />
      ) : null}

      {toast ? (
        <div
          role="status"
          style={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            padding: '6px 10px',
            background: 'var(--sp-ok)',
            color: 'var(--sp-bg, #000)',
            fontSize: 11,
            borderRadius: 3,
            boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
            maxWidth: '90%',
          }}
        >
          {toast}
        </div>
      ) : null}
    </div>
  );
}

interface ApprovalRowProps {
  request: ApprovalRequestRead;
  onAct: (req: ApprovalRequestRead, decision: 'approve' | 'deny') => void;
}

function ApprovalRow({ request, onAct }: ApprovalRowProps) {
  const isTaskTarget = request.target_type === 'task' && !!request.target_id;
  const target = isTaskTarget ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <a href={`/board/${request.target_id}`} className="mono" style={{ fontSize: 11 }}>
        task/{request.target_id!.slice(0, 8)}…
      </a>
      <button
        type="button"
        onClick={() =>
          emitInspectTask({
            taskUuid: request.target_id!,
            source: 'owner-approval-queue',
          })
        }
        title="Open this task in the Agent Run Inspector"
        style={{
          fontSize: 9,
          padding: '0 5px',
          border: '1px solid var(--sp-border)',
          background: 'transparent',
          color: 'var(--sp-text-dim)',
          borderRadius: 2,
          cursor: 'pointer',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        inspect
      </button>
    </span>
  ) : request.target_id ? (
    <span className="mono" style={{ fontSize: 11, color: 'var(--sp-text-dim)' }}>
      {request.target_type ?? 'target'}/{String(request.target_id).slice(0, 12)}
    </span>
  ) : (
    <span style={{ fontSize: 11, color: 'var(--sp-text-dim)' }}>—</span>
  );

  const risk = (request.risk_level ?? '').toLowerCase();
  const riskColor = RISK_COLOR[risk] ?? 'var(--sp-text-dim)';

  return (
    <div
      style={{
        padding: '8px 12px',
        borderBottom: '1px solid var(--sp-border)',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'baseline',
            flexWrap: 'wrap',
          }}
        >
          <span className="mono" style={{ fontSize: 11, color: 'var(--sp-text)' }}>
            {request.action_type}
          </span>
          <span
            style={{
              fontSize: 9,
              padding: '1px 5px',
              borderRadius: 2,
              background: riskColor,
              color: 'var(--sp-bg, #000)',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            {request.risk_level ?? 'unset'}
          </span>
          {target}
          {typeof request.cost_estimate === 'number' ? (
            <span style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>
              ${request.cost_estimate.toFixed(2)}
            </span>
          ) : null}
          {request.expires_at ? (
            <span style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
              expires {relativeTimeUntil(request.expires_at)}
            </span>
          ) : null}
        </div>
        {request.summary ? (
          <div style={{ fontSize: 12, color: 'var(--sp-text-muted)', marginTop: 2 }}>
            {request.summary}
          </div>
        ) : null}
        <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginTop: 2 }}>
          agent {request.agent_identity_id.slice(0, 8)}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          type="button"
          onClick={() => onAct(request, 'approve')}
          style={{
            fontSize: 11,
            padding: '4px 10px',
            border: '1px solid var(--sp-ok)',
            color: 'var(--sp-ok)',
            background: 'transparent',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          Approve
        </button>
        <button
          type="button"
          onClick={() => onAct(request, 'deny')}
          style={{
            fontSize: 11,
            padding: '4px 10px',
            border: '1px solid var(--sp-error)',
            color: 'var(--sp-error)',
            background: 'transparent',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          Deny
        </button>
      </div>
    </div>
  );
}

interface ReasonModalProps {
  decision: 'approve' | 'deny';
  request: ApprovalRequestRead;
  reason: string;
  onReasonChange: (next: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}

function ReasonModal({
  decision,
  request,
  reason,
  onReasonChange,
  onCancel,
  onSubmit,
  submitting,
  error,
}: ReasonModalProps) {
  useEscapeKey(() => {
    if (!submitting) onCancel();
  });
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div
        style={{
          width: 420,
          background: 'var(--sp-bg-panel, #111)',
          border: '1px solid var(--sp-border)',
          borderRadius: 4,
          padding: 16,
          fontSize: 12,
          color: 'var(--sp-text)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--sp-text-dim)',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            marginBottom: 4,
          }}
        >
          {decision === 'approve' ? 'Approve action' : 'Deny action'}
        </div>
        <div className="mono" style={{ fontSize: 12, marginBottom: 8 }}>
          {request.action_type}
        </div>
        {request.summary ? (
          <div style={{ color: 'var(--sp-text-muted)', marginBottom: 12 }}>
            {request.summary}
          </div>
        ) : null}
        <label style={{ display: 'block', marginBottom: 8 }}>
          <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginBottom: 4 }}>
            Reason (optional, surfaced on the board)
          </div>
          <textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            disabled={submitting}
            rows={3}
            style={{
              width: '100%',
              background: 'transparent',
              border: '1px solid var(--sp-border)',
              borderRadius: 3,
              padding: 6,
              color: 'var(--sp-text)',
              fontSize: 12,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </label>
        {error ? (
          <div style={{ color: 'var(--sp-error)', fontSize: 11, marginBottom: 8 }}>
            {error}
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              fontSize: 11,
              padding: '4px 12px',
              border: '1px solid var(--sp-border)',
              background: 'transparent',
              color: 'var(--sp-text-muted)',
              borderRadius: 3,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            style={{
              fontSize: 11,
              padding: '4px 12px',
              border: `1px solid ${decision === 'approve' ? 'var(--sp-ok)' : 'var(--sp-error)'}`,
              background: decision === 'approve' ? 'var(--sp-ok)' : 'var(--sp-error)',
              color: 'var(--sp-bg, #000)',
              borderRadius: 3,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '…' : decision === 'approve' ? 'Confirm approve' : 'Confirm deny'}
          </button>
        </div>
      </div>
    </div>
  );
}

export const ownerApprovalQueueWidget: WidgetDefinition = {
  id: 'owner-approval-queue',
  name: 'Owner Approval Queue',
  description: 'Pending agent action approvals routed to the owner or Director.',
  icon: 'shield-check',
  category: 'agents',
  scope: 'both',
  defaultSize: { w: 6, h: 6 },
  minSize: { w: 4, h: 4 },
  component: OwnerApprovalQueueWidget,
};
